import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wallet, ArrowUpRight, ArrowDownRight, Gift, Plus, TrendingUp, RefreshCw, CloudOff, Cloud } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import PartnerLayout from "./PartnerLayout";

interface WalletInfo {
  balance: number;
  lifetimeEarned: number;
  lifetimeSpent: number;
  pendingAmount: number;
  isLinkedToExternal?: boolean;
  externalSyncAvailable?: boolean;
}

interface Transaction {
  id: string;
  type: 'earned' | 'spent' | 'pending';
  amount: number;
  description: string;
  createdAt: string;
}

interface GiftCertificate {
  id: string;
  code: string;
  name: string;
  kudozCost: number;
  dollarValue: string;
  currentRedemptions: number;
  maxRedemptions: number | null;
  isActive: boolean;
  validUntil: string | null;
}

export default function PartnerKudoz() {
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const { toast } = useToast();
  
  const { data: wallet, isLoading: walletLoading, refetch: refetchWallet } = useQuery<WalletInfo>({
    queryKey: ['/api/partner/wallet/info', restaurantId],
    enabled: !!restaurantId,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/partner/wallet/sync');
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Wallet Synced",
          description: `Balance updated: ${data.balance.toLocaleString()} Kudoz`,
        });
        queryClient.invalidateQueries({ queryKey: ['/api/partner/wallet/info'] });
        queryClient.invalidateQueries({ queryKey: ['/api/partner/wallet'] });
        queryClient.invalidateQueries({ queryKey: ['/api/partner/dashboard/stats'] });
        refetchWallet();
      } else {
        toast({
          title: "Sync Issue",
          description: data.message || "Could not sync with external wallet",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync wallet balance",
        variant: "destructive",
      });
    },
  });
  
  const { data: transactions, isLoading: transactionsLoading } = useQuery<Transaction[]>({
    queryKey: ['/api/partner/wallet/transactions', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: certificates, isLoading: certificatesLoading } = useQuery<GiftCertificate[]>({
    queryKey: ['/api/partner/gift-certificates', restaurantId],
    enabled: !!restaurantId,
  });
  
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-kudoz-title">Kudoz Wallet</h1>
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground">Manage your Kudoz balance and gift certificates</p>
              {wallet?.isLinkedToExternal ? (
                <Badge variant="outline" className="gap-1">
                  <Cloud className="h-3 w-3" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <CloudOff className="h-3 w-3" />
                  Local Only
                </Badge>
              )}
            </div>
          </div>
          {wallet?.externalSyncAvailable && (
            <Button 
              variant="outline" 
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              data-testid="button-sync-wallet"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {syncMutation.isPending ? 'Syncing...' : 'Sync Balance'}
            </Button>
          )}
        </div>
        
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Current Balance</CardTitle>
              <Wallet className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              {walletLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold" data-testid="text-balance">
                  {(wallet?.balance || 0).toLocaleString()} K
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Earned</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              {walletLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold text-green-600">
                  {(wallet?.lifetimeEarned || 0).toLocaleString()} K
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
              <ArrowDownRight className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {walletLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold">
                  {(wallet?.lifetimeSpent || 0).toLocaleString()} K
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <ArrowUpRight className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              {walletLoading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <div className="text-2xl font-bold text-yellow-600">
                  {(wallet?.pendingAmount || 0).toLocaleString()} K
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="transactions">
          <TabsList>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="certificates" data-testid="tab-certificates">Gift Certificates</TabsTrigger>
          </TabsList>
          
          <TabsContent value="transactions" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Your Kudoz earning and spending history</CardDescription>
              </CardHeader>
              <CardContent>
                {transactionsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : transactions && transactions.length > 0 ? (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div 
                        key={tx.id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                        data-testid={`transaction-${tx.id}`}
                      >
                        <div className="flex items-center gap-3">
                          {tx.type === 'earned' ? (
                            <ArrowUpRight className="h-5 w-5 text-green-500" />
                          ) : tx.type === 'spent' ? (
                            <ArrowDownRight className="h-5 w-5 text-red-500" />
                          ) : (
                            <ArrowUpRight className="h-5 w-5 text-yellow-500" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(tx.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className={`font-bold ${
                          tx.type === 'earned' ? 'text-green-600' : 
                          tx.type === 'spent' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {tx.type === 'spent' ? '-' : '+'}{tx.amount.toLocaleString()} K
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No transactions yet
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="certificates" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Gift Certificates</CardTitle>
                  <CardDescription>Create redeemable certificates for customers</CardDescription>
                </div>
                <Button data-testid="button-create-certificate">
                  <Plus className="h-4 w-4 mr-1" />
                  Create Certificate
                </Button>
              </CardHeader>
              <CardContent>
                {certificatesLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : certificates && certificates.length > 0 ? (
                  <div className="space-y-3">
                    {certificates.map((cert) => (
                      <div 
                        key={cert.id} 
                        className="flex items-center justify-between p-4 rounded-lg border"
                        data-testid={`certificate-${cert.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <Gift className="h-8 w-8 text-primary" />
                          <div>
                            <p className="font-medium">{cert.name}</p>
                            <p className="text-sm text-muted-foreground">
                              Code: <span className="font-mono">{cert.code}</span>
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {cert.kudozCost.toLocaleString()} Kudoz = ${cert.dollarValue}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={cert.isActive ? "default" : "secondary"}>
                            {cert.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {cert.currentRedemptions}/{cert.maxRedemptions || '∞'} redeemed
                          </p>
                          {cert.validUntil && (
                            <p className="text-xs text-muted-foreground">
                              Expires: {new Date(cert.validUntil).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No gift certificates yet</p>
                    <p className="text-sm text-muted-foreground">
                      Create certificates to let customers redeem their Kudoz at your restaurant
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PartnerLayout>
  );
}
